import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { ThemeId } from './theme.types';
import { DEFAULT_THEME_ID, isThemeId } from './themes';
import { api } from '../api/client';
import { useAuthStore } from '../stores/auth';

const STORAGE_KEY = 'colibri_theme';

export function readStoredTheme(): ThemeId {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (isThemeId(raw)) return raw;
  } catch {
    /* localStorage unavailable */
  }
  return DEFAULT_THEME_ID;
}

interface ThemeContextValue {
  themeId: ThemeId;
  setTheme: (id: ThemeId) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  themeId: DEFAULT_THEME_ID,
  setTheme: () => {},
});

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themeId, setThemeId] = useState<ThemeId>(readStoredTheme);
  const user = useAuthStore((s) => s.user);
  const syncedUserRef = useRef<string | null>(null);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeId);
  }, [themeId]);

  // Source of truth on login / user load: server-side per-user theme.
  useEffect(() => {
    if (!user || user.id === syncedUserRef.current) return;
    syncedUserRef.current = user.id;
    if (isThemeId(user.theme)) {
      setThemeId(user.theme);
      try {
        localStorage.setItem(STORAGE_KEY, user.theme);
      } catch {
        /* ignore */
      }
    }
  }, [user]);

  const setTheme = useCallback(
    (id: ThemeId) => {
      if (id === themeId) return;
      setThemeId(id);
      try {
        localStorage.setItem(STORAGE_KEY, id);
      } catch {
        /* ignore */
      }
      // Best-effort server sync; theme stays usable offline.
      void api.patch('auth/me', { theme: id }).catch(() => {});
    },
    [themeId],
  );

  return <ThemeContext.Provider value={{ themeId, setTheme }}>{children}</ThemeContext.Provider>;
}
